export const csharpAuthExample = `using System;
using System.Collections.Generic;
using System.Net.Http;
using System.Net.Http.Headers;
using System.Security.Cryptography;
using System.Text;
using System.Text.Json;
using System.Text.Json.Serialization;
using System.Threading.Tasks;

namespace ReasonShieldIntegration
{
    class Program
    {
        // API Configuration
        private static readonly string ApiBaseUrl = "https://app.reasonshield.com/api/v1";
        private static readonly string ApiKey = Environment.GetEnvironmentVariable("VENDOR_API_KEY");
        private static readonly string WebhookSecret = Environment.GetEnvironmentVariable("VENDOR_WEBHOOK_SECRET");
        private static readonly string VendorId = Environment.GetEnvironmentVariable("VENDOR_ID");

        // Custom JsonConverter for consistent property ordering (important for signatures)
        private static readonly JsonSerializerOptions JsonOptions = new JsonSerializerOptions
        {
            PropertyNamingPolicy = JsonNamingPolicy.CamelCase,
            DefaultIgnoreCondition = JsonIgnoreCondition.WhenWritingNull,
            WriteIndented = false
        };

        static async Task Main(string[] args)
        {
            try
            {
                // Create test data
                var testData = new Dictionary<string, object>
                {
                    { "test_id", "12345" },
                    { "panel_type", "RESPIRATORY" }
                };

                // Create payload
                var payload = new Dictionary<string, object>
                {
                    { "test_data", testData }
                };

                // Submit data
                var result = await MakeAuthenticatedRequestAsync(
                    "/lis-webhook/vendor/submit-data", 
                    HttpMethod.Post, 
                    payload);

                Console.WriteLine($"Data submitted successfully. ID: {result["data_id"]}");
            }
            catch (Exception ex)
            {
                Console.WriteLine($"Error: {ex.Message}");
            }
        }

        /// <summary>
        /// Makes an authenticated request to the ReasonShield API using dual authentication
        /// </summary>
        public static async Task<Dictionary<string, object>> MakeAuthenticatedRequestAsync(
            string endpoint, HttpMethod method, Dictionary<string, object> payload = null)
        {
            string url = ApiBaseUrl + endpoint;
            
            using (var httpClient = new HttpClient())
            {
                // Create HTTP request message
                var request = new HttpRequestMessage(method, url);
                
                // Add API Key authentication
                request.Headers.Authorization = new AuthenticationHeaderValue("Bearer", ApiKey);
                
                // For methods with a payload
                if (payload != null && method != HttpMethod.Get)
                {
                    // Add required fields to payload
                    if (!payload.ContainsKey("vendor_id"))
                        payload.Add("vendor_id", VendorId);
                    
                    if (!payload.ContainsKey("timestamp"))
                        payload.Add("timestamp", DateTime.UtcNow.ToString("o"));
                    
                    if (!payload.ContainsKey("request_id"))
                        payload.Add("request_id", Guid.NewGuid().ToString());
                    
                    // Serialize payload to JSON with consistent ordering
                    string jsonPayload = JsonSerializer.Serialize(payload, JsonOptions);
                    
                    // Generate signature
                    string signature = GenerateSignature(jsonPayload, WebhookSecret);
                    
                    // Set headers
                    request.Content = new StringContent(jsonPayload, Encoding.UTF8, "application/json");
                    request.Headers.Add("X-Vendor-Signature", signature);
                }
                
                // Send the request
                var response = await httpClient.SendAsync(request);
                
                // Check response status
                if (!response.IsSuccessStatusCode)
                {
                    string errorContent = await response.Content.ReadAsStringAsync();
                    throw new Exception($"API request failed with status {response.StatusCode}: {errorContent}");
                }
                
                // Parse and return the response
                string responseContent = await response.Content.ReadAsStringAsync();
                return JsonSerializer.Deserialize<Dictionary<string, object>>(
                    responseContent, 
                    new JsonSerializerOptions { PropertyNameCaseInsensitive = true });
            }
        }

        /// <summary>
        /// Generates an HMAC-SHA256 signature for the payload
        /// </summary>
        private static string GenerateSignature(string payload, string secret)
        {
            byte[] keyBytes = Encoding.UTF8.GetBytes(secret);
            byte[] messageBytes = Encoding.UTF8.GetBytes(payload);
            
            using (var hmac = new HMACSHA256(keyBytes))
            {
                byte[] hashBytes = hmac.ComputeHash(messageBytes);
                
                // Convert bytes to hex string
                StringBuilder builder = new StringBuilder();
                for (int i = 0; i < hashBytes.Length; i++)
                {
                    builder.Append(hashBytes[i].ToString("x2"));
                }
                
                return builder.ToString();
            }
        }
    }
}`; 